<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CampaignsAPI from 'dashboard/api/campaigns';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';

const props = defineProps({
  inboxId: {
    type: [String, Number],
    default: null,
  },
  inboxOptions: {
    type: Array,
    default: () => [],
  },
  whatsappTemplateCategory: {
    type: String,
    default: 'utility',
  },
});

const emit = defineEmits(['draftsCreated']);

const { t } = useI18n();
const dialogRef = ref(null);
const isLoadingPreview = ref(false);
const isLoadingApply = ref(false);
const isLoadingDrafts = ref(false);
const previewResult = ref(null);
const applyResult = ref(null);
const batchResult = ref(null);

const state = reactive({
  days: 90,
  maxChunkSize: 300,
  selectedInboxIds: [],
});

const selectedLabelIds = computed(() =>
  (applyResult.value?.label_plan || [])
    .map(entry => entry.label_id)
    .filter(Boolean)
);

const canCreateDrafts = computed(
  () => selectedLabelIds.value.length > 0 && state.selectedInboxIds.length > 0
);

const operationParams = () => ({
  days: Number(state.days) || 90,
  max_chunk_size: Number(state.maxChunkSize) || 300,
  inbox_ids: state.selectedInboxIds,
});

const resetResults = () => {
  previewResult.value = null;
  applyResult.value = null;
  batchResult.value = null;
};

const runPreview = async () => {
  isLoadingPreview.value = true;
  try {
    const response =
      await CampaignsAPI.qualifiedAudiencePreview(operationParams());
    previewResult.value = response.data;
    applyResult.value = null;
    batchResult.value = null;
  } catch {
    useAlert(
      t(
        'CAMPAIGN.WHATSAPP.OPERATIONS.PREVIEW_ERROR',
        'Unable to preview qualified audience.'
      )
    );
  } finally {
    isLoadingPreview.value = false;
  }
};

const applyLabels = async () => {
  isLoadingApply.value = true;
  try {
    const response =
      await CampaignsAPI.qualifiedAudienceApply(operationParams());
    applyResult.value = response.data;
    previewResult.value = response.data;
    batchResult.value = null;
  } catch {
    useAlert(
      t(
        'CAMPAIGN.WHATSAPP.OPERATIONS.APPLY_ERROR',
        'Unable to apply audience labels.'
      )
    );
  } finally {
    isLoadingApply.value = false;
  }
};

const createDrafts = async () => {
  isLoadingDrafts.value = true;
  try {
    const response = await CampaignsAPI.batchDraftCampaigns({
      label_ids: selectedLabelIds.value,
      inbox_ids: state.selectedInboxIds,
      title_prefix: 'DRAFT',
      whatsapp_template_category: props.whatsappTemplateCategory,
    });
    batchResult.value = response.data;
    emit('draftsCreated');
  } catch {
    useAlert(
      t(
        'CAMPAIGN.WHATSAPP.OPERATIONS.DRAFT_ERROR',
        'Unable to create draft campaigns.'
      )
    );
  } finally {
    isLoadingDrafts.value = false;
  }
};

watch(
  () => props.inboxId,
  inboxId => {
    state.selectedInboxIds = inboxId ? [Number(inboxId)] : [];
    resetResults();
  },
  { immediate: true }
);

defineExpose({ dialogRef });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="3xl"
    overflow-y-auto
    :show-confirm-button="false"
    :title="t('CAMPAIGN.WHATSAPP.OPERATIONS.TITLE', 'Campaign operations')"
  >
    <div class="flex flex-col gap-5">
      <div class="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Input
          v-model="state.days"
          type="number"
          min="1"
          max="180"
          :label="t('CAMPAIGN.WHATSAPP.OPERATIONS.DAYS', 'Days')"
        />
        <Input
          v-model="state.maxChunkSize"
          type="number"
          min="1"
          max="300"
          :label="
            t('CAMPAIGN.WHATSAPP.OPERATIONS.CHUNK_SIZE', 'Max chunk size')
          "
        />
        <TagMultiSelectComboBox
          v-model="state.selectedInboxIds"
          :options="inboxOptions"
          :label="t('CAMPAIGN.WHATSAPP.OPERATIONS.INBOXES', 'Inboxes')"
          class="[&>div>button]:bg-n-alpha-black2"
        />
      </div>

      <div class="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="faded"
          color="slate"
          icon="i-lucide-search-check"
          :label="t('CAMPAIGN.WHATSAPP.OPERATIONS.PREVIEW', 'Preview')"
          :is-loading="isLoadingPreview"
          @click="runPreview"
        />
        <Button
          type="button"
          variant="faded"
          color="slate"
          icon="i-lucide-tags"
          :label="t('CAMPAIGN.WHATSAPP.OPERATIONS.APPLY', 'Apply labels')"
          :is-loading="isLoadingApply"
          @click="applyLabels"
        />
        <Button
          type="button"
          icon="i-lucide-files"
          :label="
            t('CAMPAIGN.WHATSAPP.OPERATIONS.CREATE_DRAFTS', 'Create drafts')
          "
          :is-loading="isLoadingDrafts"
          :disabled="!canCreateDrafts"
          @click="createDrafts"
        />
      </div>

      <div
        v-if="previewResult"
        class="grid grid-cols-2 gap-2 text-xs md:grid-cols-4"
      >
        <div class="p-3 rounded-lg border border-n-weak bg-n-alpha-1">
          <div class="text-n-slate-10">
            {{ t('CAMPAIGN.WHATSAPP.OPERATIONS.QUALIFIED', 'Qualified') }}
          </div>
          <div class="mt-1 text-base font-semibold text-n-slate-12">
            {{ previewResult.summary?.total_qualified_before_exclusions || 0 }}
          </div>
        </div>
        <div class="p-3 rounded-lg border border-n-weak bg-n-alpha-1">
          <div class="text-n-slate-10">
            {{ t('CAMPAIGN.WHATSAPP.OPERATIONS.ELIGIBLE', 'Eligible') }}
          </div>
          <div class="mt-1 text-base font-semibold text-n-teal-11">
            {{ previewResult.summary?.eligible_contacts || 0 }}
          </div>
        </div>
        <div class="p-3 rounded-lg border border-n-weak bg-n-alpha-1">
          <div class="text-n-slate-10">
            {{ t('CAMPAIGN.WHATSAPP.OPERATIONS.EXCLUDED', 'Excluded') }}
          </div>
          <div class="mt-1 text-base font-semibold text-n-ruby-11">
            {{ previewResult.summary?.excluded_contacts || 0 }}
          </div>
        </div>
        <div class="p-3 rounded-lg border border-n-weak bg-n-alpha-1">
          <div class="text-n-slate-10">
            {{ t('CAMPAIGN.WHATSAPP.OPERATIONS.CHUNKS', 'Chunks') }}
          </div>
          <div class="mt-1 text-base font-semibold text-n-slate-12">
            {{ previewResult.summary?.chunks || 0 }}
          </div>
        </div>
      </div>

      <div
        v-if="previewResult?.label_plan?.length"
        class="max-h-60 overflow-y-auto rounded-lg border border-n-weak"
      >
        <div
          v-for="entry in previewResult.label_plan"
          :key="entry.label_title"
          class="flex items-center justify-between gap-3 px-3 py-2 text-xs border-b border-n-weak last:border-b-0"
        >
          <span class="truncate text-n-slate-12">{{ entry.label_title }}</span>
          <span class="font-medium text-n-slate-11">
            {{ entry.contact_count }}
          </span>
        </div>
      </div>

      <div
        v-if="batchResult"
        class="p-3 text-sm rounded-lg border border-n-teal-6 bg-n-teal-2 text-n-teal-11"
      >
        {{
          t('CAMPAIGN.WHATSAPP.OPERATIONS.DRAFTS_CREATED', {
            count: batchResult.created_count || 0,
          })
        }}
      </div>
    </div>
  </Dialog>
</template>
